import { useRef, useState, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { convertText } from '@/utils/langConvert';
import { createChunkReplayer, type ChunkReplayer } from '@/utils/replayQueue';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { startConversation, AgentRateLimitedError } from '@/api/agent';
import { reportAgentStreamStat } from '@/api/agentStreamStat';
import { scheduleDeferredTask } from '@/utils/deferredTask';
import { AI_REQUEST_ALLOWED, buildBaziReportRequestKey, createRequestGeneration, getRateLimitPresentation, isBaziReportErrorRetryable, runAiRequestIfAllowed, type AiRequestGate, type BaziReportErrorKind } from '@/utils/aiBaziReportRequest';
import { normalizeMarkdownForRender } from '@/utils/markdownNormalize';
import { savePdfCompat } from '@/utils/pdfSave';
import { submitAiReportFeedback } from '@/api/aiReportFeedback';
import { multiLanguageHeader } from '@/utils/languageHeader';
import type { BaziReportPayload } from '@/utils/baziReportData';

export interface BaziReportPerson {
  name: string;
  gender: string;
  birthTime: string;
  lunarDate?: string;
  trueSolarTime?: string;
  birthPlace?: string;
  fourPillars?: string;
}

interface Props {
  isDark?: boolean;
  person?: BaziReportPerson;
  /** 可选：由调用方组装的完整命盘 data（buildBaziReportData 产出），随 message 一并传服务端 */
  data?: BaziReportPayload;
  /** experience 仅在排盘成功后允许请求；其他页面默认保持自动请求行为 */
  requestGate?: AiRequestGate;
}

const STREAM_URL = (import.meta.env.VITE_API_BASE as string || '') + '/api/agent/stream';

const GEN_STEPS = [
  '读取命主信息（年/月/日/时/出生地/性别）',
  '计算四柱八字与真太阳时校正',
  '推算五行强弱、格局与喜用神',
  '分析十神分布、神煞与大运流年',
  '整合白话解读并生成命理报告',
];

function formatGender(gender: string): string {
  const g = (gender || '').trim().toLowerCase();
  if (g === 'male' || g === '男') return '男';
  if (g === 'female' || g === '女') return '女';
  return gender || '';
}

function buildMessage(p: BaziReportPerson): string {
  const lines = [
    `命主：${p.name || ''}`,
    `性别：${formatGender(p.gender) || p.gender || ''}`,
    `出生时间：${p.birthTime || ''}`,
  ];
  if (p.lunarDate) lines.push(`农历：${p.lunarDate}`);
  if (p.trueSolarTime) lines.push(`真太阳时：${p.trueSolarTime}`);
  if (p.birthPlace) lines.push(`出生地：${p.birthPlace}`);
  if (p.fourPillars) lines.push(`八字：${p.fourPillars}`);
  return lines.join('\n');
}

type RequestState = 'idle' | 'requesting' | 'done' | 'error';

export default function AiBaziReport({ isDark = true, person, data, requestGate = AI_REQUEST_ALLOWED }: Props) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const trd = useCallback((text: string) => convertText(text, lang), [lang]);
  const { isLoggedIn } = useAuth();
  const requestKey = buildBaziReportRequestKey(person);
  const [expanded, setExpanded] = useState(true);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [requestState, setRequestState] = useState<RequestState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<BaziReportErrorKind | null>(null);
  const [displayContent, setDisplayContent] = useState('');
  const [replayDone, setReplayDone] = useState(false);
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);
  const [genStep, setGenStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const genStepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const replayRef = useRef<ChunkReplayer | null>(null);
  const chunksRef = useRef<string[]>([]);
  const expandedRef = useRef(true);
  const replayStartedRef = useRef(false);
  const personRef = useRef(person);
  const isLoggedInRef = useRef(isLoggedIn);
  const dataRef = useRef(data);
  const requestGenerationRef = useRef(createRequestGeneration());
  const activeGenerationRef = useRef<object | null>(null);
  const requestGeneration = requestGenerationRef.current;
  personRef.current = person;
  isLoggedInRef.current = isLoggedIn;
  dataRef.current = data;

  const cardBg = isDark ? 'var(--bg-overlay-light)' : 'rgba(245,245,247,0.9)';
  const cardBorder = 'rgba(var(--accent-primary-rgb), 0.2)';
  const rowBorder = isDark ? 'rgba(183,154,197,0.12)' : 'rgba(232,160,32,0.12)';
  const labelColor = 'var(--text-secondary)';
  const valueColor = 'var(--text-primary)';
  const accentColor = 'var(--accent-primary)';
  const goldColor = 'var(--accent-secondary)';
  const sectionBg = isDark ? 'rgba(107,78,113,0.12)' : 'rgba(245,245,247,0.6)';

  const stopStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const stopGenAnimation = useCallback(() => {
    if (genStepTimerRef.current) {
      clearTimeout(genStepTimerRef.current);
      genStepTimerRef.current = null;
    }
  }, []);

  const stopReplay = useCallback(() => {
    replayRef.current?.stop();
    replayRef.current = null;
  }, []);

  /** 5 步动画：fast 模式每步 ~1s（回播陪衬），慢速模式每步随机 10~20 秒（请求中等待），走到最后一步停留 */
  const startGenAnimation = useCallback((fast = false, generation = activeGenerationRef.current) => {
    stopGenAnimation();
    setGenStep(0);
    const scheduleNext = (step: number) => {
      if (step >= GEN_STEPS.length - 1 || generation !== activeGenerationRef.current) return;
      const delay = fast
        ? 800 + Math.floor(Math.random() * 400)
        : 10000 + Math.floor(Math.random() * 10001);
      genStepTimerRef.current = setTimeout(() => {
        if (generation !== activeGenerationRef.current) return;
        const next = step + 1;
        setGenStep(next);
        scheduleNext(next);
      }, delay);
    };
    scheduleNext(0);
  }, [stopGenAnimation]);

  /** 快速重放：5步动画（快版）+ 逐块渲染缓存 chunks（模拟流式） */
  const startReplay = useCallback((generation = activeGenerationRef.current) => {
    if (generation !== activeGenerationRef.current) return;
    stopReplay();
    setDisplayContent('');
    setPlaying(true);
    setReplayDone(false);
    startGenAnimation(true);
    const chunks = chunksRef.current;
    const replayer = createChunkReplayer(
      chunks,
      (display) => {
        if (generation === activeGenerationRef.current) setDisplayContent(display);
      },
      40,
      () => {
        if (generation === activeGenerationRef.current) setReplayDone(true);
      },
    );
    replayRef.current = replayer;
    replayStartedRef.current = true;
    replayer.start();
  }, [startGenAnimation, stopReplay]);

  // 解析 SSE data 行，返回文本片段；done 事件返回 null 标记
  const parseDataLine = useCallback((dataLine: string, eventType: string): string | null => {
    const data = dataLine.slice(5).trim();
    if (!data) return null;
    if (eventType === 'done') return '';
    if (eventType !== 'message') return null;
    try {
      const outer = JSON.parse(data);
      const innerRaw = typeof outer === 'object' ? outer.content : outer;
      if (typeof innerRaw === 'string' && innerRaw) {
        try {
          const inner = JSON.parse(innerRaw);
          const chunk = typeof inner === 'string' ? inner : (inner.content ?? '');
          return chunk || null;
        } catch {
          return innerRaw;
        }
      }
    } catch {
      // 忽略无法解析的事件
    }
    return null;
  }, []);

  const runStream = useCallback(async (
    conversationId: string,
    signal: AbortSignal,
    onChunk: (chunk: string) => void,
    onDone: () => void,
    onStats?: (stat: { status: 'success' | 'error'; error?: string; t1Ms: number; t2Ms: number; requestId: string }) => void,
    requestPerson?: BaziReportPerson,
    requestData?: BaziReportPayload,
  ) => {
    const t0 = Date.now();
    let firstEventMs: number | null = null;
    let firstMessageMs: number | null = null;
    let reported = false;
    let streamRequestId = '';
    const recordEvent = (eventName: string) => {
      if (firstEventMs === null) firstEventMs = Date.now() - t0;
      if (eventName === 'message' && firstMessageMs === null) firstMessageMs = Date.now() - t0;
    };
    // 上报一次即止：收到首正文后立即上报（success），后续流结束不再补报；未收到正文的失败路径走 error
    const reportOnce = (status: 'success' | 'error', error?: string) => {
      if (reported) return;
      reported = true;
      try {
        onStats?.({
          status,
          error,
          t1Ms: firstEventMs ?? -1,
          t2Ms: firstMessageMs ?? -1,
          requestId: streamRequestId,
        });
      } catch { /* 上报失败不影响主流程 */ }
    };
    const body: Record<string, unknown> = {
      appId: 'fateuni',
      agentType: 1,
      conversationId,
      message: buildMessage(requestPerson || personRef.current as BaziReportPerson),
    };
    if (requestData) {
      body.data = JSON.stringify(requestData);
    }
    let res: Response;
    try {
      res = await fetch(STREAM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...multiLanguageHeader() },
        credentials: 'include',
        body: JSON.stringify(body),
        signal,
      });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') throw e;
      reportOnce('error', (e as Error)?.message || 'stream request failed');
      throw e;
    }
    if (!res.ok || !res.body) {
      reportOnce('error', `HTTP ${res.status}`);
      throw new Error(trd(`请求失败（HTTP ${res.status}）`));
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let eventType = '';
    while (true) {
      const { done: readerDone, value } = await reader.read();
      if (readerDone) break;
      buffer += decoder.decode(value, { stream: true });
      let newlineIdx: number;
      while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIdx).trim();
        buffer = buffer.slice(newlineIdx + 1);
        if (!line || line.startsWith(':')) continue;
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
          recordEvent(eventType);
        } else if (line.startsWith('data:')) {
          if (eventType === 'start') {
            // start 事件 content 是 {"type":"start","conversation_id":"...","request_id":"..."}，
            // 外层 data 是 {"content":"<json字符串>"}，双层 JSON 提取 request_id
            try {
              const outer = JSON.parse(line.slice(5).trim());
              const inner = JSON.parse(outer.content || '');
              if (inner.request_id) streamRequestId = inner.request_id;
            } catch { /* 忽略无法解析的 start 事件 */ }
            continue;
          }
          const chunk = parseDataLine(line, eventType);
          if (chunk !== null) {
            if (chunk === '') { onDone(); return; }
            // 收到首个正文片段即上报（t1/t2 均已确定），后续不再上报
            reportOnce('success');
            onChunk(chunk);
          }
        }
      }
    }
    onDone();
    reportOnce('success');
  }, [parseDataLine, trd]);

  /** 预取：SSE 接收 → chunks 队列（不渲染）；完成后若已展开则自动快速重放 */
  const prefetchStream = useCallback(async () => {
    if (!requestKey || !personRef.current?.name) return;
    if (!runAiRequestIfAllowed(requestGate, () => undefined)) return;
    const generation = requestGenerationRef.current.start();
    activeGenerationRef.current = generation;
    const requestPerson = personRef.current;
    const requestData = dataRef.current;
    const isCurrent = () => requestGenerationRef.current.isCurrent(generation);
    stopStream(); // 中止上一次请求
    stopReplay();
    stopGenAnimation();
    chunksRef.current = [];
    replayStartedRef.current = false;
    setDisplayContent('');
    setPlaying(false);
    setGenStep(0);
    setError(null);
    setErrorKind(null);
    setRequestState('requesting');
    // 请求中且已展开：播慢版五步动画等待（message 到达后 startReplay 自动切换到正文）。
    // 注：prefetchStream 在「排盘完成后挂载即请求」+「默认展开」时由挂载 effect 触发，
    // 不会经过 handleToggle 的 requesting 分支，需在此显式启动动画。
    if (isCurrent() && expandedRef.current && !replayStartedRef.current) {
      setPlaying(true);
      startGenAnimation(false, generation);
    }
    const controller = new AbortController();
    abortRef.current = controller;
    let conversationId: string;
    try {
      conversationId = await startConversation(controller.signal);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError' || controller.signal.aborted) {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        return;
      }
      if (!isCurrent()) return;
      if (err instanceof AgentRateLimitedError) {
        const presentation = getRateLimitPresentation(isLoggedInRef.current);
        if (presentation === 'inline-expanded') {
          setError(trd('今日 AI 命理解读次数已用完，请明日再来'));
          setErrorKind('rate-limited');
          expandedRef.current = true;
          setExpanded(true);
        } else {
          setShowRateLimitModal(true);
        }
        setRequestState('error');
        setPlaying(false);
        setGenStep(0);
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        return;
      }
      // 其他错误（网络/服务异常）：降级为随机 conversationId 继续，不阻塞
      conversationId = `fateuni-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }

    if (controller.signal.aborted || abortRef.current !== controller || !isCurrent()) return;

    try {
      await runStream(
        conversationId,
        controller.signal,
        (chunk) => {
          if (!isCurrent()) return;
          chunksRef.current.push(chunk);
          // 请求进行中：已获取正文（message 片段）且用户已展开 → 立即开始回播，后续增量追加
          if (expandedRef.current && !replayStartedRef.current) {
            startReplay(generation);
          }
        },
        () => {
          if (!isCurrent()) return;
          setRequestState('done');
          // 请求完成：若正在回播，标记流结束（播完剩余后 done）
          if (replayRef.current) {
            replayRef.current.complete();
          }
          // 若用户已展开但尚未开始回播（请求完成前没有正文），立即回播全部
          if (expandedRef.current && !replayStartedRef.current) {
            startReplay(generation);
          }
        },
        (stat) => {
          if (!isCurrent()) return;
          reportAgentStreamStat({
            conversationId: stat.requestId || '',
            agentType: 1,
            message: buildMessage(requestPerson as BaziReportPerson),
            status: stat.status,
            error: stat.error,
            t1Ms: stat.t1Ms,
            t2Ms: stat.t2Ms,
          });
        },
        requestPerson,
        requestData,
      );
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      if (!isCurrent()) return;
      // 停止回播，避免 replayer 持续轮询空转
      stopReplay();
      replayStartedRef.current = false;
      setError((err as Error)?.message || trd('AI 报告生成失败，请重试'));
      setErrorKind('request-failed');
      setRequestState('error');
    } finally {
      // 只清理自己的 controller，避免 StrictMode 下旧请求的 finally 误 abort 新请求
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, [requestKey, requestGate, runStream, stopStream, stopReplay, stopGenAnimation, startReplay, startGenAnimation, trd]);

  // 挂载（即排盘完成后）立即请求服务端，数据入队列；卸载清理
  useEffect(() => {
    let cancelScheduledRequest = () => {};
    if (requestKey) {
      runAiRequestIfAllowed(requestGate, () => {
        cancelScheduledRequest = scheduleDeferredTask(() => {
          void prefetchStream();
        });
      });
    }
    return () => {
      cancelScheduledRequest();
      requestGeneration.invalidate();
      activeGenerationRef.current = null;
      stopStream();
      stopGenAnimation();
      stopReplay();
    };
  }, [requestKey, requestGate, prefetchStream, requestGeneration, stopStream, stopGenAnimation, stopReplay]);

  // 点击标题：展开/折叠。展开时数据就绪则快速重放，请求中则播 5 步动画（正文到达自动回播）
  const handleToggle = () => {
    setExpanded(v => {
      const next = !v;
      expandedRef.current = next;
      if (next) {
        if (requestState === 'done') {
          startReplay(activeGenerationRef.current);
        } else if (requestState === 'requesting') {
          // 已在回播则保持；否则 message 未到，播慢版 5 步动画等待（10~20s/步）
          if (!replayStartedRef.current) {
            setPlaying(true);
            startGenAnimation(false);
          }
        }
      } else {
        stopReplay();
        stopGenAnimation();
        setPlaying(false);
        replayStartedRef.current = false;
      }
      return next;
    });
  };

  const handleFeedback = async (type: 'like' | 'dislike') => {
    if (!requestKey) return;
    const next = feedback === type ? null : type;
    setFeedback(next);
    try {
      await submitAiReportFeedback(requestKey, next ?? 'cancel');
    } catch {
      // 上报失败不阻塞交互，静默忽略
    }
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current || isDownloading) return;
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: isDark ? '#160C24' : '#FFF8EC',
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.getAttribute('data-pdf-ignore') === 'true',
      });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageHeightPx = Math.floor((pageHeight * canvas.width) / pageWidth);
      const pageCanvas = document.createElement('canvas');
      const ctx = pageCanvas.getContext('2d');
      pageCanvas.width = canvas.width;
      let rendered = 0;
      let pageIndex = 0;
      while (rendered < canvas.height) {
        const sliceHeight = Math.min(pageHeightPx, canvas.height - rendered);
        pageCanvas.height = sliceHeight;
        ctx?.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx?.drawImage(canvas, 0, rendered, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, pageWidth, (sliceHeight * pageWidth) / canvas.width);
        rendered += sliceHeight;
        pageIndex += 1;
      }
      savePdfCompat(pdf, `${person?.name || trd('命主')}${trd('八字命理报告')}.pdf`);
    } finally {
      setIsDownloading(false);
    }
  };

  const personName = person?.name || trd('命主');
  const isDone = requestState === 'done';
  const isRequesting = requestState === 'requesting';
  const showLoading = playing && !displayContent;
  const streaming = isRequesting && expanded && playing;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      {/* 標題欄 */}
      <button
        type="button"
        onClick={handleToggle}
        className={`w-full flex items-center justify-between px-2 md:px-5 py-2 md:py-4 text-left cursor-pointer transition-colors rounded-2xl ${isDark ? 'bg-[#2A1F0A]/60 hover:bg-[#3A2F1A]/80' : 'bg-amber-50 hover:bg-amber-100'}`}
      >
        <div className="flex items-center gap-2">
          <div
            className={`w-6 h-6 rounded-md flex items-center justify-center ${
              isDark ? 'bg-gold-500/20' : 'bg-[#4a4a4c]'
            }`}
          >
            <i className="ri-sparkling-line text-xs md:text-sm text-white" />
          </div>
          <span
            className={`text-base md:text-sm font-medium ${
              isDark ? 'text-white' : 'text-[#1c1c1e]'
            }`}
          >
            {trd('AI 命理报告')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!expanded && (
            <span
              className={`text-xs md:text-sm font-bold ${
                isDark ? 'text-amber-400' : 'text-amber-700'
              }`}
            >
              {trd('点击生成报告')}
            </span>
          )}
          <i
            className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
              expanded ? 'rotate-180' : ''
            } ${isDark ? 'text-amber-400' : 'text-amber-700'}`}
          />
        </div>
      </button>

      {/* 展開內容 */}
      {expanded && (
        <div className="border-t" style={{ borderColor: rowBorder }}>
          <div ref={reportRef} className="px-4 py-4 space-y-4">
            <div className="space-y-2 pb-3" style={{ borderBottom: `1px solid ${rowBorder}` }}>
              <h3 className="text-lg font-bold" style={{ color: valueColor, fontFamily: "'Noto Serif TC', serif" }}>
                {personName} · {trd('八字命理报告')}
              </h3>

              {error ? (
                <div className="rounded-lg p-4 space-y-3" style={{ background: sectionBg, border: `1px solid ${rowBorder}` }}>
                  <p className="text-sm leading-relaxed" style={{ color: '#D14B45' }}>
                    {error}
                  </p>
                  {isBaziReportErrorRetryable(errorKind) && (
                    <button
                      type="button"
                      onClick={() => prefetchStream()}
                      className="px-4 py-2 rounded-full text-xs font-bold cursor-pointer"
                      style={{ background: 'linear-gradient(135deg, #E8A020, #C07010)', color: '#fff' }}
                    >
                      {trd('重新生成')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="rounded-lg p-3 space-y-1.5 mt-2" style={{ background: sectionBg, border: `1px solid ${rowBorder}` }}>
                  {displayContent ? (
                    <div className="text-sm leading-relaxed markdown-body" style={{ color: valueColor }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeMarkdownForRender(displayContent)}</ReactMarkdown>
                    </div>
                  ) : showLoading || streaming ? (
                    <div className="space-y-2.5 py-2">
                      <div className="flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-sm" style={{ color: accentColor }} />
                        <span className="text-sm font-bold" style={{ color: accentColor }}>
                          {trd('AI 正在生成报告...')}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {GEN_STEPS.map((step, idx) => {
                          const isActive = idx === genStep;
                          const isStepDone = idx < genStep;
                          return (
                            <div key={idx} className="flex items-center gap-2">
                              <span
                                className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0"
                                style={{ color: isStepDone ? '#34C759' : accentColor }}
                              >
                                {isStepDone ? (
                                  <i className="ri-check-line text-[0.6rem]" />
                                ) : isActive ? (
                                  <i className="ri-loader-4-line animate-spin text-[0.6rem]" />
                                ) : null}
                              </span>
                              <span
                                className="text-xs leading-relaxed"
                                style={{
                                  color: isActive ? valueColor : isStepDone ? labelColor : 'var(--text-tertiary)',
                                  fontWeight: isActive ? 600 : 400,
                                }}
                              >
                                {trd(step)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : isDone ? (
                    <p className="text-sm" style={{ color: labelColor }}>
                      {trd('报告已生成完成')}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2 py-2">
                      <i className="ri-loader-4-line animate-spin text-sm" style={{ color: accentColor }} />
                      <span className="text-sm" style={{ color: labelColor }}>
                        {trd('准备生成报告...')}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 點讚 / 差評 */}
          {(requestState === 'done' && replayDone) && (
            <div className="px-4 pb-2 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => handleFeedback('like')}
                className="w-10 h-10 rounded-full flex items-center justify-center border transition-all active:scale-95 cursor-pointer"
                style={{
                  borderColor: feedback === 'like'
                    ? (isDark ? 'rgba(255,64,129,0.5)' : 'rgba(233,30,99,0.4)')
                    : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'),
                  background: feedback === 'like'
                    ? (isDark ? 'rgba(255,64,129,0.15)' : 'rgba(233,30,99,0.08)')
                    : 'transparent',
                  color: feedback === 'like'
                    ? (isDark ? '#FF4081' : '#E91E63')
                    : (isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)'),
                }}
              >
                <i className="ri-thumb-up-line text-base" />
              </button>
              <button
                type="button"
                onClick={() => handleFeedback('dislike')}
                className="w-10 h-10 rounded-full flex items-center justify-center border transition-all active:scale-95 cursor-pointer"
                style={{
                  borderColor: feedback === 'dislike'
                    ? (isDark ? 'rgba(250,204,21,0.4)' : 'rgba(245,158,11,0.4)')
                    : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'),
                  background: feedback === 'dislike'
                    ? (isDark ? 'rgba(250,204,21,0.15)' : 'rgba(245,158,11,0.12)')
                    : 'transparent',
                  color: feedback === 'dislike'
                    ? (isDark ? '#FDE68A' : '#B45309')
                    : (isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)'),
                }}
              >
                <i className="ri-thumb-down-line text-base" />
              </button>
            </div>
          )}

          {/* 下載 PDF 按鈕 */}
          <div className="px-4 pb-4">
            <button
              type="button"
              data-pdf-ignore="true"
              onClick={handleDownloadPdf}
              disabled={isDownloading || !(requestState === 'done' && replayDone)}
              className="w-full h-10 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-50 cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #C53D43, #A12D33)',
                color: '#fff',
                boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.25)' : '0 12px 24px rgba(197,61,67,0.18)',
              }}
            >
              <i className={`${isDownloading ? 'ri-loader-4-line animate-spin' : 'ri-file-pdf-2-line'} text-base`} />
              <span className="text-sm font-bold" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                {isDownloading ? trd('正在生成报告...') : trd('下载报告 PDF')}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* 每日限流拦截框（未登录引导登录） */}
      {showRateLimitModal && (
        <div
          className="fixed inset-0 z-[500] flex items-center justify-center px-6"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowRateLimitModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-3xl p-6 shadow-2xl"
            style={{
              background: isDark ? '#1A0F2E' : '#fff',
              border: isDark ? '1px solid rgba(232,195,158,0.2)' : '1px solid #e5e5ea',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{
                  background: isDark ? 'rgba(232,195,158,0.12)' : 'rgba(232,160,32,0.1)',
                  border: isDark ? '1px solid rgba(232,195,158,0.25)' : '1px solid rgba(232,160,32,0.25)',
                }}
              >
                <i
                  className="ri-lock-password-line text-2xl"
                  style={{ color: isDark ? '#E8C39E' : '#C07010' }}
                />
              </div>
            </div>
            <h3
              className="text-base font-bold text-center mb-2"
              style={{ color: isDark ? '#fff' : '#1c1c1e', fontFamily: "'Noto Serif TC', serif" }}
            >
              {trd('登录后可为更多人排盘')}
            </h3>
            <p
              className="text-sm text-center leading-relaxed mb-5"
              style={{ color: isDark ? '#A0A0A8' : '#6e6e73' }}
            >
              {trd('游客身份每次仅能为')}
              <span className="font-bold mx-0.5" style={{ color: isDark ? '#E8C39E' : '#C07010' }}>
                1{trd('位命主')}
              </span>
              {trd('排盘。登录后可切换命主，查看多份命盘报告')}
            </p>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => {
                  setShowRateLimitModal(false);
                  navigate('/login');
                }}
                className="w-full py-3 rounded-2xl text-sm font-bold transition-all active:scale-95 cursor-pointer"
                style={{
                  background: isDark
                    ? 'linear-gradient(135deg, #E8C39E, #D4A870)'
                    : 'linear-gradient(135deg, #E8A020, #C07010)',
                  color: isDark ? '#1A0F2E' : '#fff',
                  boxShadow: '0 4px 16px rgba(232,160,32,0.3)',
                  fontFamily: "'Noto Serif TC', serif",
                  letterSpacing: 2,
                }}
              >
                {trd('立即登录')}
              </button>
              <button
                onClick={() => setShowRateLimitModal(false)}
                className="w-full py-2.5 rounded-2xl text-sm font-medium transition-all active:scale-95 cursor-pointer"
                style={{ color: isDark ? '#A0A0A8' : '#6e6e73', background: isDark ? 'rgba(255,255,255,0.05)' : 'transparent' }}
              >
                {trd('暂不登录，继续查看当前命盘')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
